import React, { useEffect, useState } from "react";
import { Modal, DatePicker, Button, Table, Space, Descriptions, Row, Col, message, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Title } = Typography;

// Row-level surplus %, per the report spec: (delivered - scheduled) / scheduled x 100.
// This is deliberately a different base than the header's overall surplus %
// (which is delivered-based) - each is what was asked for at that level.
const surplusPercent = (scheduled, delivered) => {
  if (!scheduled) return null;
  return ((delivered - scheduled) / scheduled) * 100;
};

const renderPercent = (value) =>
  value === null || value === undefined ? "-" : `${value.toFixed(1)}%`;

const formatTime = (time) => (time ? time.slice(0, 5) : "-");

const pluralize = (count, unit) => `${count || 0} ${unit}${count === 1 ? "" : "s"}`;

const CampaignReportModal = ({ campaign, closeModal }) => {
  const campaignStart = dayjs(campaign.start_date);
  const campaignEnd = dayjs(campaign.end_date);

  const [range, setRange] = useState([
    campaignStart,
    campaignEnd.isAfter(dayjs()) ? dayjs() : campaignEnd,
  ]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);

  const fetchReport = async () => {
    if (!range || range.length !== 2) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${campaign.id}/reports/delivery`,
        {
          params: {
            start_date: range[0].format("YYYY-MM-DD"),
            end_date: range[1].format("YYYY-MM-DD"),
          },
        }
      );
      setReport(response.data.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      message.error(err.response?.data?.message || "Failed to load report");
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${campaign.id}/reports/delivery/download`,
        {
          params: {
            start_date: range[0].format("YYYY-MM-DD"),
            end_date: range[1].format("YYYY-MM-DD"),
          },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `campaign-${campaign.id}-delivery-report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (err) {
      setDownloading(false);
      message.error("Failed to download report");
    }
  };

  // Report range can't extend outside the campaign's own scheduled window.
  const disabledDate = (current) =>
    current && (current.isBefore(campaignStart, "day") || current.isAfter(campaignEnd, "day"));

  const commonScheduleColumns = [
    {
      title: "Start",
      dataIndex: "start_time",
      render: formatTime,
    },
    {
      title: "End",
      dataIndex: "end_time",
      render: formatTime,
    },
    {
      title: "Slot",
      dataIndex: "slot_duration_seconds",
      render: (v) => (v ? `${v}s` : "-"),
    },
  ];

  const overallColumns = [
    { title: "City", dataIndex: "city_name" },
    { title: "Property Count", dataIndex: "property_count" },
    { title: "Households", dataIndex: "households" },
    { title: "Screens", dataIndex: "screens" },
    { title: "Active", dataIndex: "active" },
    ...commonScheduleColumns,
    { title: "Scheduled", dataIndex: "scheduled_impressions" },
    { title: "Delivered", dataIndex: "delivered_impressions" },
    {
      title: "Surplus",
      key: "surplus_percentage",
      render: (_, row) => renderPercent(surplusPercent(row.scheduled_impressions, row.delivered_impressions)),
    },
  ];

  const cityWiseColumns = [
    { title: "City", dataIndex: "city_name" },
    { title: "Property", dataIndex: "location_name" },
    { title: "Households", dataIndex: "households" },
    { title: "Screens", dataIndex: "screens" },
    { title: "Active", dataIndex: "active" },
    ...commonScheduleColumns,
    { title: "Scheduled", dataIndex: "scheduled_impressions" },
    { title: "Delivered", dataIndex: "delivered_impressions" },
    {
      title: "Surplus",
      key: "surplus_percentage",
      render: (_, row) => renderPercent(surplusPercent(row.scheduled_impressions, row.delivered_impressions)),
    },
  ];

  const propertyWiseColumns = [
    { title: "City", dataIndex: "city_name" },
    { title: "Property", dataIndex: "location_name" },
    { title: "Screen ID", dataIndex: "device_code" },
    { title: "Households", dataIndex: "households" },
    { title: "Screens", key: "screens", render: () => 1 },
    ...commonScheduleColumns,
    { title: "Scheduled", dataIndex: "scheduled_impressions" },
    { title: "Delivered", dataIndex: "delivered_impressions" },
    {
      title: "Surplus",
      key: "surplus_percentage",
      render: (_, row) => renderPercent(surplusPercent(row.scheduled_impressions, row.delivered_impressions)),
    },
  ];

  // Grouped the same way the Excel export breaks these down - one
  // Property-Wise table per city, then one Screen-Wise table per property
  // within it (see campaign.controller.js's buildDeliveryReportWorkbook) -
  // rather than one flat table each across every city/property.
  const cityGroups = report?.city_summary || [];

  return (
    <Modal
      open
      title={`Delivery Report - ${campaign.title}`}
      onCancel={closeModal}
      footer={null}
      width={1100}
    >
      <Space style={{ marginBottom: 16 }}>
        <span>Report period:</span>
        <RangePicker
          value={range}
          onChange={(dates) => setRange(dates)}
          format="DD-MM-YYYY"
          disabledDate={disabledDate}
        />
        <Button type="primary" onClick={fetchReport} loading={loading}>
          View
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleDownload} loading={downloading}>
          Download Excel
        </Button>
      </Space>

      {report && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Descriptions size="small" bordered column={1}>
                <Descriptions.Item label="Campaign">{campaign.title}</Descriptions.Item>
                <Descriptions.Item label="Campaign Duration">
                  {pluralize(report.header.campaign_duration_working_days, "day")}
                </Descriptions.Item>
                <Descriptions.Item label="Duration Left">
                  {pluralize(report.header.duration_left_days, "day")}
                  {report.header.duration_left_hours ? ` ${pluralize(report.header.duration_left_hours, "hour")}` : ""}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date">
                  {campaignStart.format("DD-MM-YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="End Date">{campaignEnd.format("DD-MM-YYYY")}</Descriptions.Item>
                <Descriptions.Item label="Report Period">
                  {range[0].format("DD-MM-YYYY")} - {range[1].format("DD-MM-YYYY")}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions size="small" bordered column={1}>
                <Descriptions.Item label="Scheduled Impressions">
                  {report.header.scheduled_impressions}
                </Descriptions.Item>
                <Descriptions.Item label="Delivered Impressions">
                  {report.header.delivered_impressions}
                </Descriptions.Item>
                <Descriptions.Item label="Surplus">{renderPercent(report.header.surplus_percentage)}</Descriptions.Item>
                <Descriptions.Item label="Absolute Reach">{report.header.absolute_reach}</Descriptions.Item>
                <Descriptions.Item label="Repeat Reach">{report.header.repeat_reach}</Descriptions.Item>
                <Descriptions.Item label="Downtime (hours)">{report.header.downtime_hours}</Descriptions.Item>
                <Descriptions.Item label="Impressions in Downtime">
                  {report.header.impressions_in_downtime}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          <Title level={5}>Overall Summary</Title>
          <Table
            size="small"
            loading={loading}
            columns={overallColumns}
            dataSource={report.city_summary}
            rowKey="city_id"
            pagination={false}
            style={{ marginBottom: 24 }}
          />

          <Title level={5}>Property Wise Summary</Title>
          {cityGroups.map((city) => (
            <React.Fragment key={city.city_id}>
              <Title level={5} style={{ fontSize: 14 }}>
                {city.city_name}
              </Title>
              <Table
                size="small"
                loading={loading}
                columns={cityWiseColumns}
                dataSource={city.locations || []}
                rowKey="location_id"
                pagination={false}
                style={{ marginBottom: 24 }}
              />
            </React.Fragment>
          ))}

          <Title level={5}>Screen Wise Summary</Title>
          {cityGroups.flatMap((city) => city.locations || []).map((loc) => (
            <React.Fragment key={loc.location_id}>
              <Title level={5} style={{ fontSize: 14 }}>
                {loc.location_name}
              </Title>
              <Table
                size="small"
                loading={loading}
                columns={propertyWiseColumns}
                dataSource={(loc.devices || []).map((d) => ({
                  ...d,
                  location_name: loc.location_name,
                  city_name: loc.city_name,
                  key: d.device_id,
                }))}
                rowKey="key"
                pagination={false}
                style={{ marginBottom: 24 }}
              />
            </React.Fragment>
          ))}
        </>
      )}
    </Modal>
  );
};

export default CampaignReportModal;
